import { pickWords, sortTheWords } from "./Utility";
import { MY_BACKEND_URL } from "../../Backend/supabaseClient";
const BACKEND_BASE_URL = MY_BACKEND_URL;

// -------------- helpers --------------

function splitToWords(text) {
    // Keeps letters + apostrophes/hyphens inside words; removes other punctuation
    // Example: "It's co-operate" -> ["It's","co-operate"]
    return (text || "")
        .replace(/[^\p{L}\p{N}'-]+/gu, " ")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
}

/**
 * Network call to your backend.
 * Adjust URL + headers as needed.
 */
export async function fetchPronunciations(words) {
    const res = await fetch(`${BACKEND_BASE_URL}/api/pronunce`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            // Authorization: `Bearer ${token}`, // if needed
        },
        body: JSON.stringify({ words }),
    });

    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Pronounce API failed (${res.status}): ${txt || res.statusText}`);
    }

    return res.json(); // expected { locale, count, results:[{word, ipa, respell}] }
}

/**
 * Merge results into scored words.
 * Works even when backend doesn't return indexes.
 *
 * If your request sends unique words (recommended), mapping by `word` is perfect.
 * If backend might normalize case differently, we map case-insensitively.
 */
function mergePronunciations(scored, apiJson) {
    const results = apiJson?.results || [];

    // Case-insensitive map: "Apple" and "apple" both map to same entry.
    // If you want strict case-sensitive matching, remove .toLowerCase() parts.
    const map = new Map();
    for (const r of results) {
        const k = String(r.word || "").toLowerCase();
        if (!k) continue;
        // keep first or last; either is fine because it's same word pronunciation result
        map.set(k, { ipa: r.ipa ?? null, respell: r.respell ?? "" });
    }

    return scored.map((item) => {
        const k = String(item.word).toLowerCase();
        const found = map.get(k);
        return {
            ...item,
            ipa: found?.ipa ?? null,
            respell: found?.respell ?? "",
        };
    });
}

// -------------- your public function --------------

/**
 * Pipeline:
 * 1) split text -> words
 * 2) pickWords -> unique cleaned words (you already have this)
 * 3) sortTheWords -> scored sorted list (you already have this)
 * 4) call backend with the UNIQUE words (same ones you scored)
 * 5) merge ipa/respell back into the scored list
 *
 * Returns: [{ word, score, ipa, respell, ...any other scoring fields }]
 */
export async function getPronounciation(text) {
    const tokens = splitToWords(text);

    // Your functions
    const unique = pickWords(tokens);         // returns unique words (important!)
    const scored = sortTheWords(unique);      // returns [{word, score, ...}]

    // Call backend with EXACTLY these unique words (ensures 1:1 mapping by word)
    const apiJson = await fetchPronunciations(unique);

    // Merge pronunciations into the scored array
    const merged = mergePronunciations(scored, apiJson);

    return merged;
}
